import moment from 'moment';
import { map, Observable, of, tap } from 'rxjs';

import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  FieldConfig,
  FilterOperator,
  FilterType,
  FilterValue,
  PagedResultDto,
} from '@ideapa/common-interfaces';

@Injectable()
export abstract class BaseApiClient<T> {
  abstract baseUrl: string;

  constructor(protected http: HttpClient) {}

  protected CACHE_EXPIRATION_MIN = 5;
  protected _pagedCache: Map<
    string,
    { data: PagedResultDto<T>; expiration: Date }
  > = new Map();

  getAll() {
    return this.http.get<T[]>(this.baseUrl);
  }

  getPaged(
    take: number,
    skip: number,
    orderBy?: { [field: string]: 'asc' | 'desc' },
    filters?: FilterValue[],
    join?: string[],
    areDomainUserFiltersDisabled?: boolean,
    isEntitySelectionLimited?: boolean,
    areEnteAnnoFiltersDisabled?: boolean
  ) {
    const mappedFilters = this.buildPagedFilters(filters);

    const request = {
      skip,
      take,
      orderBy,
      filters: mappedFilters,
      join,
      areDomainUserFiltersDisabled: areDomainUserFiltersDisabled,
      isEntitySelectionLimited: isEntitySelectionLimited,
      areEnteAnnoFiltersDisabled: areEnteAnnoFiltersDisabled,
    };

    // console.log('request: ', request);
    const cacheKey = JSON.stringify(request);
    const cached = this._pagedCache.get(cacheKey);
    if (cached) {
      if (cached.expiration > new Date()) {
        return of(cached.data);
      } else {
        this._pagedCache.delete(cacheKey);
      }
    }

    return this.http
      .post<PagedResultDto<T>>(`${this.baseUrl}/paged`, request)
      .pipe(
        tap((result) =>
          this._pagedCache.set(cacheKey, {
            data: result,
            expiration: new Date(
              Date.now() + 60 * 1000 * this.CACHE_EXPIRATION_MIN
            ),
          })
        )
      );
  }

  protected buildPagedFilters(filters: FilterValue[] | undefined) {
    return filters?.map((filter) => {
      if (filter.type !== FilterType.datetime) return filter;
      else {
        switch (filter.operator) {
          case FilterOperator.equals:
            return {
              ...filter,
              operator: FilterOperator.inRange,
              value: [
                moment(filter.value[0]).startOf('d').toISOString(),
                moment(filter.value[0]).endOf('d').toISOString(),
              ],
            };
          case FilterOperator.greaterThan:
            return {
              ...filter,
              value: [moment(filter.value[0]).endOf('d').toISOString()],
            };
          case FilterOperator.greaterThanOrEqual:
            return {
              ...filter,
              value: [moment(filter.value[0]).startOf('d').toISOString()],
            };
          case FilterOperator.lessThan:
            return {
              ...filter,
              value: [moment(filter.value[0]).startOf('d').toISOString()],
            };
          case FilterOperator.lessThanOrEqual:
            return {
              ...filter,
              value: [moment(filter.value[0]).endOf('d').toISOString()],
            };

          case FilterOperator.inRange:
            return {
              ...filter,
              value: [
                moment(filter.value[0]).startOf('d').toISOString(),
                moment(filter.value[1]).endOf('d').toISOString(),
              ],
            };

          default:
            return {
              ...filter,
            };
        }
      }
    });
  }

  downloadExcel(
    fileName?: string,
    columns?: FieldConfig[],
    filters?: FilterValue[],
    orderBy?: { [field: string]: 'asc' | 'desc' },
    join?: string[]
  ): Observable<ArrayBuffer> {
    return this.http
      .post(
        this.baseUrl + '/download-excel',
        { fileName, columns, filters: filters || [], orderBy, join },
        {
          responseType: 'arraybuffer',
        }
      )
      .pipe(
        map((file: ArrayBuffer) => {
          return file;
        })
      );
  }

  downloadPdf(
    fileName?: string,
    columns?: FieldConfig[],
    filters?: FilterValue[],
    orderBy?: { [field: string]: 'asc' | 'desc' },
    join?: string[]
  ): Observable<ArrayBuffer> {
    return this.http
      .post(
        this.baseUrl + '/download-pdf',
        { fileName, columns, filters, orderBy, join },
        {
          responseType: 'arraybuffer',
        }
      )
      .pipe(
        map((file: ArrayBuffer) => {
          return file;
        })
      );
  }
}
